import type {
  FieldSpec,
  ModifyAction,
  ModifyActionOption,
  ModifyResourceId,
  SourceGroup,
  SourceId,
  SourceSpec,
  SubOptionId,
  WizardFlow
} from './types'
import { TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS } from '@atbash-labs/ontology-core'

import type { UploadableIngestJobKind } from '../util/mergeIngestJobs'
import { soapAuthKindFromTagMode, soapAuthUsesPassword } from './soap-auth'

export const SOURCE_GROUPS: SourceGroup[] = [
  {
    title: 'SCADA & gateway',
    items: [
      {
        id: 'scada',
        label: 'SCADA / gateway backup',
        desc: 'Gateway backup export — the tag providers everything else binds to.',
        subLabel: 'Source system',
        // Ignition first — keep this order in sync with StepCollect's
        // SourceSubSeg so the toggle doesn't flip sides between steps.
        subOptions: [
          { id: 'ignition', label: 'Ignition' },
          { id: 'aveva', label: 'Aveva' }
        ]
      }
    ]
  },
  {
    title: 'Control & motion',
    items: [
      {
        id: 'plc',
        label: 'PLC code',
        desc: 'Controller exports — .l5x, .acd, .st, .gxw.',
        children: [
          {
            id: 'ipmap',
            label: 'IP / network map',
            desc: 'Devices and OPC servers mapped to their PLCs.',
            recommended: true
          }
        ]
      },
      {
        id: 'robotics',
        label: 'Robotics code',
        desc: 'Yaskawa, FANUC, or Yamaha controller backups.',
        children: [
          {
            id: 'roboticsmap',
            label: 'Robotics controller → PLC mapping',
            desc: 'Which robot controller is driven by which PLC.',
            recommended: true
          }
        ]
      },
      {
        id: 'vision',
        label: 'Vision system code',
        desc: 'Cognex In-Sight or DataMan backups — cameras and their inspection programs.',
        children: [
          {
            id: 'visionmap',
            label: 'Vision system → PLC mapping',
            desc: 'Which PLC module each camera is, read from the controller’s I/O tree.',
            recommended: true
          }
        ]
      },
      {
        id: 'experion',
        label: 'Experion ERDB + EMDB',
        desc: 'Honeywell Experion PKS control (ERDB) + optional enterprise/PCN (EMDB) — one-shot graph ingest.'
      }
    ]
  },
  {
    title: 'Machine telemetry',
    items: [
      {
        id: 'mtconnect',
        label: 'MTConnect server',
        desc: 'Agent probe URL and live DataItem streams.'
      }
    ]
  },
  {
    title: 'Historians',
    items: [
      {
        id: 'pi',
        label: 'PI historian',
        desc: 'PI Web API points as standalone tags, or PI history bound to existing Ignition / OPC tags.'
      },
      {
        id: 'canary',
        label: 'Canary historian',
        desc: 'Canary Labs Views tags as standalone read-through historian tags, or Canary history bound to existing Ignition / OPC tags.',
        subLabel: 'Authentication',
        subOptions: [
          { id: 'api_token', label: 'API token' },
          { id: 'user', label: 'Username + password' },
          { id: 'anonymous', label: 'Anonymous (HTTP)' }
        ]
      }
    ]
  },
  {
    title: 'Databases',
    items: [
      {
        id: 'dbdump',
        label: 'DB dump',
        desc: 'Excel or CSV dump queried as a stand-in database.'
      },
      {
        id: 'sql',
        label: 'SQL database',
        desc: 'Persistent login — register postgres, SQL Server, or MySQL as a live database connection.',
        subLabel: 'Engine',
        subOptions: [
          { id: 'postgres', label: 'PostgreSQL' },
          { id: 'sqlserver', label: 'SQL Server' },
          { id: 'mysql', label: 'MySQL' }
        ]
      }
    ]
  },
  {
    title: 'Safety',
    items: [
      {
        id: 'srs',
        label: 'Safety Requirements Specification',
        desc: 'SRS Access database (.accdb) — protective functions only, no live-data links.'
      },
      {
        id: 'safetymanager',
        label: 'Safety Manager dump',
        desc: 'Honeywell Safety Manager — legacy Excel points dump or Safety Builder 15.2 SQL database.',
        subLabel: 'Version',
        subOptions: [
          { id: 'legacy', label: 'Legacy' },
          { id: '15.2', label: '15.2' }
        ]
      },
      {
        id: 'safetymap',
        label: 'Safety Manager ↔ SRS mapping',
        desc: 'Deterministically links separately ingested SRS functions and equipment to Safety Manager FLDs and points.'
      }
    ]
  },
  {
    title: 'Business systems',
    items: [
      {
        id: 'erp',
        label: 'ERP data',
        desc: 'Orders, materials, and cost records.',
        subLabel: 'Connection',
        subOptions: [
          { id: 'ignition', label: 'Connected through Ignition' },
          { id: 'standalone', label: 'Standalone DB' }
        ]
      },
      {
        id: 'mes',
        label: 'MES datasources',
        desc: 'Production, batch, and downtime records.',
        subLabel: 'Connection',
        subOptions: [
          { id: 'ignition', label: 'Connected through Ignition' },
          { id: 'standalone', label: 'Standalone DB' }
        ]
      }
    ]
  },
  {
    title: 'Documentation',
    items: [
      {
        id: 'unstructured',
        label: 'Unstructured documentation',
        desc: 'P&ID drawings, manuals, SOPs, and reports — files or a zip.'
      },
      {
        id: 'tabular',
        label: 'Tabular file',
        desc: 'Daily reports, tally sheets, and exports — read with a saved config into typed rows.'
      }
    ]
  },
  {
    title: 'Integrations',
    items: [
      {
        id: 'soap',
        label: 'SOAP web service',
        desc: 'Probe a WSDL, register endpoint credentials, and ingest its operations.'
      },
      {
        id: 'pss',
        label: 'Hexagon PlantState Suite',
        desc: 'PSS / PSI SOAP service mirrored into SQL tables — tags, limits, and the alarm & event journal, refreshed on a schedule.'
      },
      {
        id: 'servicenow',
        label: 'ServiceNow',
        desc: 'Connect a ServiceNow instance for incidents and technician work orders.'
      }
    ]
  }
]

/**
 * Sources with collect UI / specs ready but no ingest job yet.
 * Kept in SOURCE_GROUPS + specFor; filtered out of the picker.
 */
export const HIDDEN_SOURCES = new Set<SourceId>(['erp', 'mes'])

export interface IngestSourceFlags {
  experion: boolean
  srs: boolean
  safetyManager: boolean
  /** Canary Labs Views historian (`ui.ingest.canary`). Hidden when absent. */
  canary?: boolean
}

/** ERP/MES plus gated sources whose entitlements are off. */
export function ingestSourcesHiddenByFlags(flags: IngestSourceFlags): Set<SourceId> {
  const hidden = new Set<SourceId>(HIDDEN_SOURCES)
  if (!flags.experion) hidden.add('experion')
  if (!flags.srs) hidden.add('srs')
  if (!flags.safetyManager) hidden.add('safetymanager')
  // Mapping can be run later against already-ingested site data, so keep it
  // discoverable when either source family is enabled.
  if (!flags.srs && !flags.safetyManager) hidden.add('safetymap')
  if (!flags.canary) hidden.add('canary')
  if (!flags.experion && !flags.srs) hidden.add('tagmap')
  return hidden
}

export function visibleSourceGroups(
  groups: SourceGroup[] = SOURCE_GROUPS,
  hidden: ReadonlySet<SourceId> = HIDDEN_SOURCES
): SourceGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !hidden.has(item.id))
    }))
    .filter((group) => group.items.length > 0)
}

export function visibleModResources(
  hidden: ReadonlySet<SourceId> = HIDDEN_SOURCES
): typeof MOD_RESOURCES {
  return MOD_RESOURCES.filter((res) => !hidden.has(res.id as SourceId))
}

export const SOURCE_ORDER: SourceId[] = [
  'scada',
  'opctags',
  'plc',
  'plcupdate',
  'pssupdate',
  'ipmap',
  'robotics',
  'roboticsmap',
  'vision',
  // Needs both the controller (PLC code) and the cameras (vision system code).
  'visionmap',
  'experion',
  'mtconnect',
  'pi',
  'canary',
  'dbdump',
  'sql',
  'soap',
  'pss',
  'srs',
  'safetymanager',
  'safetymap',
  'tagmap',
  'erp',
  'mes',
  'unstructured',
  'tabular',
  'servicenow'
]

export const SOURCE_LABELS: Record<SourceId, string> = {
  scada: 'SCADA / gateway backup',
  opctags: 'Tag import',
  plc: 'PLC code',
  plcupdate: 'Update PLC',
  pssupdate: 'Update PSS mirror',
  ipmap: 'IP / network map',
  robotics: 'Robotics code',
  roboticsmap: 'Robotics controller → PLC mapping',
  vision: 'Vision system code',
  visionmap: 'Vision system → PLC mapping',
  mtconnect: 'MTConnect server',
  pi: 'PI historian',
  canary: 'Canary historian',
  dbdump: 'DB dump',
  sql: 'SQL database',
  soap: 'SOAP web service',
  pss: 'Hexagon PlantState Suite',
  experion: 'Experion ERDB + EMDB',
  srs: 'Safety Requirements Specification',
  safetymanager: 'Safety Manager dump',
  safetymap: 'Safety Manager ↔ SRS mapping',
  tagmap: 'Create Tag Mapping',
  erp: 'ERP data',
  mes: 'MES datasources',
  unstructured: 'Unstructured documentation',
  tabular: 'Tabular file',
  servicenow: 'ServiceNow'
}

const ROBOT_VENDORS = [
  {
    id: 'Yaskawa',
    label: 'Yaskawa (Motoman)',
    accept: '.jbi,.dat,.cnd,.prm,.csv,.zip',
    helper: 'Motoman backup: .JBI, IONAME.DAT, .CND, .PRM, or a .zip of the backup.'
  },
  {
    id: 'FANUC',
    label: 'FANUC',
    accept: '.ls,.kl,.io,.vd,.vda,.csv,.zip',
    helper:
      'FANUC backup: .LS (TP), .KL (KAREL), iRVision vision data (.VD, .VDA), I/O config (for example DIOCFGSV.IO), EtherNet/IP .csv, or a .zip.'
  },
  {
    id: 'Yamaha',
    label: 'Yamaha',
    accept: '.prg,.pos,.pnt,.txt,.csv,.zip',
    helper:
      'Yamaha backup: .prg (BASIC), .pos/.pnt (points), I/O tables (*io*.txt), .csv, or a .zip.'
  }
]

function F(id: string, label: string, opts?: Partial<FieldSpec>): FieldSpec {
  return {
    id,
    label,
    placeholder: '',
    required: false,
    type: 'text',
    span: 1,
    ...opts
  }
}

export const GATEWAY_LINK_FIELDS: FieldSpec[] = [
  F('gatewayLinkCertificate', 'Gateway client certificate (PEM)', {
    required: true,
    pem: 'certificate',
    span: 2,
    help: 'Select gateway-link-client.crt exported by the Gateway Link module. This is a public certificate; no private key is uploaded.'
  }),
  F('gatewayLinkGatewayId', 'Gateway certificate identity (CA uploads only)', {
    span: 2,
    help: 'Leave blank when uploading the gateway client certificate. If uploading its issuing CA instead, enter the common name from the gateway client certificate.'
  })
]

export function specFor(
  id: SourceId,
  sub?: SubOptionId,
  vendorId?: string,
  fileName?: string | null,
  tagMode?: string,
  flow: WizardFlow = 'new',
  fileCount = fileName ? 1 : 0
): SourceSpec {
  const isZip = !!fileName && /\.zip$/i.test(fileName)
  switch (id) {
    case 'scada': {
      const aveva = sub === 'aveva'
      const mode = tagMode || 'webdev'
      const opc = mode === 'opcua'
      const modify = flow === 'modify'
      return {
        drop: {
          label: 'Structured ingest',
          helper: aveva
            ? 'AVEVA Galaxy .bak backup. Diff preview is skipped for .bak uploads.'
            : 'Ignition gateway backup (.gwbk) or project .zip export.',
          accept: aveva ? '.bak' : '.gwbk,.zip'
        },
        note:
          modify && !aveva
            ? 'Acquisition settings apply only to newly detected tags. Existing tag registrations keep their current configuration and can be changed from the Tags page.'
            : mode === 'gateway_link'
              ? 'An administrator must supply the proxy certificate and keys. They are stored in the encrypted app secret store and shared by this environment. Configure the module to trust these credentials and connect to the proxy listener. Ingest checks the connection before writing tags.'
              : undefined,
        seg: aveva
          ? undefined
          : {
              label: modify ? 'New tag acquisition' : 'History source',
              key: 'tagMode',
              after: true,
              options: [
                { id: 'webdev', label: 'WebDev' },
                { id: 'opcua', label: 'Ignition OPC UA' },
                { id: 'gateway_link', label: 'Gateway Link' }
              ]
            },
        fields: aveva
          ? [
              F('gatewayUrl', 'Gateway URL', {
                placeholder: 'https://gateway.example.com:8043',
                span: 2,
                required: true,
                help: 'You must specify a protocol and port.'
              }),
              F('avevaSqlServer', 'AVEVA SQL server', {
                placeholder: 'Default: localhost,1433 (or AVEVA_SQL_SERVER)'
              }),
              F('avevaSqlUser', 'AVEVA SQL user', {
                placeholder: 'Default: sa (or AVEVA_SQL_USER)'
              }),
              F('avevaSqlPassword', 'AVEVA SQL password', {
                type: 'password',
                placeholder: 'Overrides AVEVA_SQL_PASSWORD for this upload'
              }),
              F('avevaBakHostDir', 'AVEVA backup host folder', {
                placeholder: 'Absolute path to folder mounted at /backup'
              })
            ]
          : modify
            ? [
                F('gatewayUrl', 'Gateway URL', {
                  placeholder: 'https://gateway.example.com:8043',
                  span: 2,
                  required: true,
                  help: 'Used to extract the uploaded backup. Existing acquisition settings are preserved.'
                }),
                F('gatewayId', 'Gateway ID (optional)', {
                  placeholder: 'existing-gateway-id',
                  span: 2,
                  help: 'Use the existing gateway ID to upsert into that gateway. Leave blank to derive it from the Gateway URL.'
                }),
                ...(mode === 'gateway_link'
                  ? GATEWAY_LINK_FIELDS
                  : [
                      F('webDevProject', 'WebDev project', {
                        placeholder: 'Axilon'
                      })
                    ]),
                ...(opc
                  ? [
                      F('opcUaEndpointUrl', 'OPC UA endpoint URL (optional)', {
                        span: 2,
                        placeholder: 'opc.tcp://gateway-host:62541',
                        help: 'Used only for newly detected tags. Leave blank to derive it from the backup’s OPC bind port and the gateway host.'
                      })
                    ]
                  : [])
              ]
            : [
                F('gatewayUrl', 'Gateway URL', {
                  placeholder: 'https://gateway.example.com:8043',
                  span: 2,
                  required: true,
                  help: 'You must specify a protocol and port.'
                }),
                ...(mode === 'gateway_link'
                  ? GATEWAY_LINK_FIELDS
                  : [
                      F('webDevProject', 'WebDev project', {
                        placeholder: 'Axilon'
                      }),
                      F('webDevUsername', 'WebDev username', {
                        placeholder: 'username'
                      }),
                      F('webDevPassword', 'WebDev password', {
                        type: 'password',
                        placeholder: 'password',
                        span: 2,
                        help: 'Basic auth the proxy sends on HTTP calls to this gateway’s /system/webdev/* endpoints. Editable later under Settings → Gateways.'
                      })
                    ]),
                ...(opc
                  ? [
                      F('opcUaUsername', 'OPC UA username', {
                        placeholder: 'Default: opcuauser (or OPCUA_USERNAME)'
                      }),
                      F('opcUaPassword', 'OPC UA password', {
                        type: 'password',
                        placeholder: 'Overrides OPCUA_PASSWORD for this gateway'
                      }),
                      F('opcUaEndpointUrl', 'OPC UA endpoint URL (optional)', {
                        span: 2,
                        placeholder: 'opc.tcp://gateway-host:62541',
                        help: 'Leave blank to derive it from the backup’s OPC bind port and the gateway host.'
                      })
                    ]
                  : mode === 'mqtt'
                    ? [
                        F('mqttBroker', 'MQTT broker URL', {
                          placeholder: 'ssl://broker-host:8883',
                          span: 2,
                          help: 'Sparkplug B namespace on this broker is subscribed for tag readings.'
                        }),
                        F('mqttUsername', 'MQTT username', {
                          placeholder: 'username'
                        }),
                        F('mqttPassword', 'MQTT password', {
                          type: 'password',
                          placeholder: 'password'
                        })
                      ]
                    : [])
              ],
        checks: modify
          ? [
              {
                id: 'enableScrapingNew',
                label: 'Enable scraping on newly registered tags',
                help: 'Existing tags keep their acquisition configuration. Newly registered tags start disabled unless this is checked.',
                default: false
              }
            ]
          : aveva || mode === 'webdev'
            ? []
            : [
                {
                  id: 'enableScraping',
                  label: 'Enable scraping of tags',
                  help: 'Registers this gateway’s tags for live scraping right after ingest. Uncheck to register them disabled and turn scraping on later from the Tags page.',
                  default: true
                }
              ]
      }
    }
    case 'opctags': {
      const directOpc = tagMode === 'opc'
      return {
        drop: {
          label: 'OPC tag list',
          helper: directOpc
            ? 'Upload direct OPC UA addresses and ExpandedNodeIDs. Every endpoint is verified before registration.'
            : 'Upload OPC addresses that the selected Ignition gateway reads through its WebDev relay.',
          accept: '.csv,.tsv,.xlsx'
        },
        schema: {
          required: directOpc
            ? ['opc_server', 'opc_item_path', 'opc_node_id', 'name']
            : ['opc_server', 'opc_item_path', 'name'],
          // Direct OPC lists opc_endpoint_url as optional because the endpoint
          // field below covers a file that omits it. A per-row value still
          // wins, which is the only way to span several OPC servers in one file.
          optional: directOpc
            ? [
                'opc_endpoint_url',
                'description',
                'data_type',
                'units',
                'category',
                'equipment_hint'
              ]
            : ['description', 'data_type', 'units', 'category', 'equipment_hint'],
          templateProtocol: directOpc ? 'opc' : 'webdev'
        },
        seg: {
          label: 'OPC transport',
          key: 'tagMode',
          options: [
            { id: 'webdev', label: 'WebDev relay' },
            { id: 'opc', label: 'Direct OPC UA' }
          ]
        },
        fields: [
          F('gatewayId', 'Existing gateway ID', {
            placeholder: 'default-gateway',
            span: 2,
            help: 'Tags are attached to this existing gateway in the ontology. Leave blank to use default-gateway.'
          }),
          F('defaultOpcServer', 'Default OPC server (optional)', {
            span: 2,
            placeholder: 'Ignition OPC UA Server',
            help: 'Applied to rows that omit opc_server.'
          }),
          ...(directOpc
            ? [
                F('defaultOpcEndpointUrl', 'Default OPC endpoint URL', {
                  placeholder: 'opc.tcp://opc.example.com:4840',
                  span: 2,
                  help: 'Applied to rows that omit opc_endpoint_url. Required unless the file carries the column — supply it here so one file works from any network.'
                }),
                F('opcUaUsername', 'OPC UA username (optional)', {
                  placeholder: 'operator',
                  help: 'Applied to every endpoint in this import. Split the file if endpoints use different credentials.'
                }),
                F('opcUaPassword', 'OPC UA password (optional)', {
                  type: 'password',
                  placeholder: 'password',
                  help: 'Sent only for verification and registration; never stored in the ingest job.'
                })
              ]
            : [
                F('gatewayUrl', 'Gateway URL', {
                  placeholder: 'https://gateway.example.com:8043',
                  span: 2,
                  required: true,
                  help: 'The existing Ignition gateway that will service opc-relay reads.'
                }),
                F('webDevProject', 'WebDev project', { placeholder: 'Axilon' })
              ])
        ],
        checks: [
          {
            id: 'defaultEnabled',
            label: 'Enable history collection',
            help: 'Leave off to register tags for on-demand reads only.',
            default: false
          }
        ]
      }
    }
    case 'plc':
      return {
        drop: {
          label: 'PLC program ingest',
          helper:
            'Select one or more Rockwell L5X/ACD, Siemens ST, or Mitsubishi GX Works2 .gxw files, or a .zip archive. Each PLC file starts its own job.',
          accept: '.l5x,.st,.acd,.gxw,.zip'
        },
        note:
          fileCount > 1
            ? 'Controller IDs will be inferred from each selected PLC filename. Each file starts its own ingest job and they run one after another.'
            : isZip
              ? 'Controller IDs will be inferred from each PLC filename in the zip. Each file starts its own ingest job and they run one after another.'
              : undefined,
        fields: [
          F('controllerId', 'Controller ID (optional)', {
            span: 2,
            placeholder: 'e.g. PLC-17',
            help:
              fileCount > 1
                ? 'Multi-file uploads infer one controller ID per file, so this value is ignored.'
                : isZip
                  ? 'Zip uploads infer one controller ID per file, so this value is ignored.'
                  : 'Overrides the controller ID inferred from the filename.'
          })
        ]
      }
    case 'ipmap':
      return {
        drop: {
          label: 'PLC ↔ SCADA mapping document',
          helper:
            'Single xlsx / csv upload. Links SCADA Compute (OPC/device) nodes to PLC Controllers via the MAPS_TO_CONTROLLER edge.',
          accept: '.xlsx,.csv'
        },
        fields: []
      }
    case 'robotics': {
      const v = ROBOT_VENDORS.find((x) => x.id === vendorId) || ROBOT_VENDORS[0]
      return {
        seg: {
          label: 'Controller vendor',
          key: 'vendor',
          options: ROBOT_VENDORS.map((x) => ({ id: x.id, label: x.label })),
          resetsFile: true
        },
        drop: {
          label: 'Robot program ingest',
          helper: v.helper,
          accept: v.accept
        },
        fields: [
          F('controllerId', 'Controller ID (optional)', {
            span: 2,
            placeholder: 'e.g. Robot-01',
            help: 'Overrides the controller ID inferred from the backup filename.'
          })
        ],
        checks: [
          {
            id: 'aiInference',
            label: 'Infer missing details with AI (experimental)',
            help: 'After the deterministic parse, runs a best-effort AI pass to fill ambiguous details. Inferred fields are flagged needs_review.',
            default: false
          }
        ]
      }
    }
    case 'vision':
      return {
        drop: {
          label: 'Vision system backup',
          helper:
            'Cognex In-Sight jobs (.job, .jobx), In-Sight backups (.bak or a zipped <camera>.000 folder), DataMan backups (.dmb, .cfg, .cdc), VisionPro .vpp, or a .zip of several cameras. Images are not ingested yet. FANUC iRVision runs on the robot, so it ingests with Robotics code → FANUC.',
          accept: '.job,.jobx,.bak,.dmb,.cfg,.cdc,.vpp,.zip'
        },
        note: 'Cameras are linked to PLC tags by the Vision system → PLC mapping step, which reads the controller’s I/O tree.',
        fields: [
          F('deviceName', 'Camera name (optional)', {
            placeholder: 'e.g. LabelCam',
            help: 'Names a camera whose files don’t identify it, such as a single loose .job file.'
          }),
          F('plcModuleName', 'PLC module name (optional)', {
            placeholder: 'e.g. LabelCam',
            help: 'The camera’s module name in the PLC I/O tree, for the mapping step. Used when the upload holds one camera.'
          }),
          F('ipAddress', 'Camera IP address (optional)', {
            span: 2,
            placeholder: 'e.g. 192.168.1.50',
            help: 'Matched against the module’s Ethernet address when names differ. Used when the upload holds one camera.'
          })
        ]
      }
    case 'visionmap':
      return {
        drop: {
          label: 'Controller export (.L5X)',
          helper:
            'The Studio 5000 L5X whose I/O tree declares the cameras — usually the same file ingested as PLC code. Links each camera to its controller and module connection tags (Cam:I1, Cam:O1).',
          accept: '.l5x'
        },
        note: 'Runs after the PLC code and the vision system code, which must be in this run or already ingested.',
        fields: [
          F('controllerId', 'Controller ID (optional)', {
            span: 2,
            placeholder: 'e.g. Line1_PLC',
            help: 'Only needed when the PLC code was ingested under an ID other than the L5X file name or its controller name.'
          })
        ]
      }
    case 'roboticsmap':
      return {
        drop: {
          label: 'Robotics ↔ PLC mapping document',
          helper:
            'Single xlsx / csv upload. Links PLC tags to robot I/O signals via the MAPS_TO_ROBOT_SIGNAL edge (PLCTag → RobotIOSignal).',
          accept: '.xlsx,.csv'
        },
        fields: []
      }
    case 'srs':
      return {
        drop: {
          label: 'SRS Access database',
          helper:
            'Single .accdb upload. ProtectiveFunction graph only — no Ignition, JDE, or PI links.',
          accept: '.accdb'
        },
        note: 'Optional Access password is used for this upload and never stored. Site comes from the wizard; tblPlant fills in the plant name on the Site node.',
        fields: [
          F('password', 'Access password', {
            type: 'password',
            span: 2,
            placeholder: 'Leave blank if the database is unencrypted',
            help: 'Sent with the file only. Never written to the job payload or logs.'
          })
        ]
      }
    case 'safetymanager':
      if (sub === '15.2') {
        return {
          note: 'One-shot SQL login to a Safety Builder 15.2 plant database. Every loaded Safety Manager controller is ingested (or one System ID). Credentials are not stored. FLD membership follows the plant backup — incomplete FLD linkage in the current dump is a known source limitation (points with no FLD land as Unassigned).',
          fields: [
            F('server', 'SQL Server *', {
              span: 2,
              required: true,
              placeholder: 'localhost',
              help: 'Host, host,port (localhost,1433), or host\\instance.'
            }),
            F('database', 'Database *', {
              required: true,
              placeholder: '3GI_SIS'
            }),
            F('username', 'SQL username *', {
              required: true,
              placeholder: 'sm_reader'
            }),
            F('password', 'SQL password *', {
              required: true,
              type: 'password'
            }),
            F('systemId', 'System ID', {
              span: 2,
              placeholder: 'e.g. 47R2902_04S17',
              help: 'Optional. Restrict to one controller node name or cabinet System ID. Leave blank to ingest every loaded SM.'
            })
          ]
        }
      }
      return {
        drop: {
          label: 'Safety Manager points dump',
          helper: 'Single .xls / .xlsx Points export. MERGEs LogicSolver + FLDs + SafetyPoints.',
          accept: '.xls,.xlsx'
        },
        note: 'Legacy Excel dump. System ID is optional when the filename is NNR####_* (e.g. 47R2902_04S18.xls → 47-RIE-2902). This does not create SIFs, HAZOP scenarios, or cross-source links.',
        fields: [
          F('systemId', 'System ID', {
            span: 2,
            placeholder: 'e.g. 47-RIE-2902',
            help: 'Logic-solver cabinet. Inferred from the dump filename when left blank.'
          })
        ]
      }
    case 'safetymap':
      return {
        note: 'Runs after both independent source ingests. Matches normalized ISA identities within this site and owns the IMPLEMENTED_BY and MAPS_TO_SAFETY_POINT edges.',
        fields: []
      }
    case 'tagmap':
      return {
        note: 'Modify-site only. AI reads the selected mapping type from the existing site, proposes one or more grammars, and previews links before you apply them.',
        fields: []
      }
    case 'mtconnect':
      return {
        note: 'Connection only — no file. The Agent is probed, its Device and Component tree ingested, and its DataItems registered for live streaming.',
        fields: [
          F('agentUrl', 'Agent URL *', {
            span: 2,
            required: true,
            placeholder: 'http://machine-agent.example.com:5000',
            help: 'The service probes /probe before creating the ingest job.'
          }),
          F('username', 'Basic-auth username', {}),
          F('password', 'Basic-auth password', { type: 'password' })
        ]
      }
    case 'pi':
      return {
        note: 'Connection only — no file. Create standalone PI tags, or discover and approve PI history bindings for existing Ignition and OPC tags.',
        seg: {
          label: 'Ingest mode',
          key: 'tagMode',
          options: [
            { id: 'standalone', label: 'Standalone PI tags' },
            {
              id: 'existing_tags',
              label: 'Historian for existing tags'
            }
          ]
        },
        fields: [
          F('baseUrl', 'PI Web API base URL *', {
            span: 2,
            required: true,
            placeholder: 'https://pi.example.com/piwebapi',
            help: 'Full PI Web API root. Credentials must be entered separately, never embedded in the URL.'
          }),
          F('username', 'Basic-auth username *', { required: true }),
          F('password', 'Basic-auth password *', {
            required: true,
            type: 'password'
          }),
          F('nameFilter', 'Point name filter', {
            span: 2,
            placeholder: 'Optional PI wildcard, for example Line1*',
            help: 'Passed to PI Web API as nameFilter. Leave blank to ingest every Data Archive point.'
          })
        ],
        checks: [
          {
            id: 'skipTlsVerify',
            label: 'Skip TLS certificate verification',
            help: 'Use only for a PI Web API endpoint with a trusted-by-you self-signed development certificate.',
            default: false
          }
        ]
      }
    case 'canary': {
      const auth = sub === 'user' || sub === 'anonymous' ? sub : 'api_token'
      return {
        note:
          auth === 'anonymous'
            ? 'Connection only — no file. Anonymous access uses the Views HTTP port (55235). Create standalone Canary tags, or discover and approve Canary history bindings for existing Ignition and OPC tags. Nothing is mirrored.'
            : 'Connection only — no file. Credentials travel in Views request bodies, so use the HTTPS port (55236). Create standalone Canary tags, or discover and approve Canary history bindings for existing Ignition and OPC tags. Nothing is mirrored.',
        seg: {
          label: 'Ingest mode',
          key: 'tagMode',
          options: [
            { id: 'standalone', label: 'Standalone Canary tags' },
            { id: 'existing_tags', label: 'Historian for existing tags' }
          ]
        },
        fields: [
          F('baseUrl', 'Canary Views base URL *', {
            span: 2,
            required: true,
            placeholder:
              auth === 'anonymous'
                ? 'http://canary.example.com:55235'
                : 'https://canary.example.com:55236',
            help: 'Canary Views web API host. The port defaults to 55236 (https) or 55235 (http); a trailing /api/v2 is ignored. Never embed credentials in the URL.'
          }),
          ...(auth === 'api_token'
            ? [
                F('apiToken', 'API token *', {
                  span: 2,
                  required: true,
                  type: 'password',
                  help: 'Canary Identity API token bound to a Views user. Stored only in the proxy secret store.'
                })
              ]
            : []),
          ...(auth === 'user'
            ? [
                F('username', 'Username *', { required: true }),
                F('password', 'Password *', {
                  required: true,
                  type: 'password',
                  help: 'Exchanged for a Views user token; the password itself is stored only in the proxy secret store.'
                })
              ]
            : []),
          F('rootPath', 'Root path', {
            span: 2,
            placeholder: 'Optional, for example Plant or Plant.{Process}',
            help: 'Canary view, view.{dataset}, or deeper folder to browse from. Leave blank to ingest every view on the server.'
          }),
          F('nameFilter', 'Tag name filter', {
            span: 2,
            placeholder: 'Optional Canary search, for example Line1*',
            help: 'Passed to Canary Views browseTags as search. Leave blank to ingest every tag under the root path.'
          })
        ],
        checks: [
          {
            id: 'skipTlsVerify',
            label: 'Skip TLS certificate verification',
            help: 'Use only for a Canary Views endpoint with a trusted-by-you self-signed development certificate.',
            default: false
          }
        ]
      }
    }
    case 'dbdump':
      return {
        drop: {
          label: 'Spreadsheet dump',
          helper:
            'Excel workbook or CSV/TSV. Each sheet becomes a table; headers and a few rows preview below.',
          accept: '.xlsx,.xls,.csv,.tsv'
        },
        fields: [
          F('connectionName', 'Connection name *', {
            span: 2,
            required: true,
            placeholder: 'orders-dump',
            help: 'Becomes the database name agents query (urn:db:<name>). Use a stable name you can retarget later.'
          })
        ]
      }
    case 'sql':
      return {
        note: 'Registers a live database connection. Creates the same standalone Database node a spreadsheet dump uses (urn:db:<name>) and stores credentials on the existing db_connections table so Plant View and agents can query it later.',
        fields: [
          F('connectionName', 'Connection name *', {
            span: 2,
            required: true,
            placeholder: 'mes-prod',
            help: 'Becomes the database name agents query (urn:db:<name>). Use a stable name you can retarget later.'
          }),
          F('server', 'Host *', {
            span: 2,
            required: true,
            placeholder:
              sub === 'mysql'
                ? 'localhost:3306'
                : sub === 'sqlserver'
                  ? 'localhost,1433'
                  : 'localhost:5432',
            help: 'Host, host:port, host,port, host\\instance, or a postgres:// / mysql:// / jdbc:… URL.'
          }),
          F('database', 'Database *', {
            required: true,
            placeholder: sub === 'sqlserver' ? 'MES' : 'mes'
          }),
          F('username', 'Username *', {
            required: true,
            placeholder: 'axilon_reader'
          }),
          F('password', 'Password *', { required: true, type: 'password' })
        ]
      }
    case 'soap': {
      const passwordAuth = soapAuthUsesPassword(soapAuthKindFromTagMode(tagMode))
      return {
        note: 'Connection only — no file. Preview probes the WSDL without creating a job. Start ingest registers the endpoint, then writes its endpoint, contract, and operations to the review branch.',
        seg: {
          label: 'Authentication',
          key: 'tagMode',
          options: [
            { id: 'none', label: 'None' },
            { id: 'basic', label: 'Basic auth' },
            { id: 'ntlm', label: 'NTLM' }
          ]
        },
        fields: [
          F('endpointName', 'Endpoint name *', {
            span: 2,
            required: true,
            placeholder: 'maintenance-erp'
          }),
          F('wsdlUrl', 'WSDL URL *', {
            span: 2,
            required: true,
            placeholder: 'https://erp.example.com/services/Maintenance?wsdl',
            help: 'HTTP or HTTPS only. Do not embed credentials in the URL.'
          }),
          ...(passwordAuth
            ? [
                F('username', tagMode === 'ntlm' ? 'Username *' : 'Basic-auth username *', {
                  required: true,
                  placeholder: tagMode === 'ntlm' ? 'NORTH\\soap-user' : undefined,
                  ...(tagMode === 'ntlm'
                    ? {
                        help: 'Use DOMAIN\\username when the account is domain-qualified.'
                      }
                    : {})
                }),
                F('password', tagMode === 'ntlm' ? 'Password *' : 'Basic-auth password *', {
                  required: true,
                  type: 'password',
                  help: 'Sent only to the proxy credential store; never written to ingest job payloads or logs.'
                })
              ]
            : [])
        ]
      }
    }
    case 'pss': {
      const passwordAuth = soapAuthUsesPassword(soapAuthKindFromTagMode(tagMode))
      return {
        note: 'Connection only — no file. Start ingest creates a tracked job that checks the PlantState Suite service, imports every data owner and tag manifest, then copies the selected alarm & event journals in logged 1-hour intervals. The proxy keeps the managed SQL mirror refreshed afterward.',
        seg: {
          label: 'Authentication',
          key: 'tagMode',
          options: [
            { id: 'none', label: 'None' },
            { id: 'basic', label: 'Basic auth' },
            { id: 'ntlm', label: 'NTLM' }
          ]
        },
        fields: [
          F('connectionName', 'Connection name *', {
            span: 2,
            required: true,
            placeholder: 'pss-plant-a',
            help: 'Becomes the database name agents query (urn:db:<name>). Use a stable name.'
          }),
          F('serviceUrl', 'Service URL *', {
            span: 2,
            required: true,
            placeholder: 'http://pss.example.com/PlantStateSuite/plantstatesuite.asmx',
            help: 'The plantstatesuite.asmx endpoint (HTTP or HTTPS). Do not embed credentials in the URL.'
          }),
          ...(passwordAuth
            ? [
                F('username', tagMode === 'ntlm' ? 'Username *' : 'Basic-auth username *', {
                  required: true,
                  placeholder: tagMode === 'ntlm' ? 'PLANT\\pss-reader' : undefined,
                  ...(tagMode === 'ntlm'
                    ? {
                        help: 'Use DOMAIN\\username when the account is domain-qualified.'
                      }
                    : {})
                }),
                F('password', tagMode === 'ntlm' ? 'Password *' : 'Basic-auth password *', {
                  required: true,
                  type: 'password',
                  help: 'Sent only to the proxy credential store; never written to ingest job payloads or logs.'
                })
              ]
            : []),
          F('lookbackHours', 'Journal lookback (hours)', {
            placeholder: '720',
            help: 'How far back the first copy of the alarm & event journal reaches. Default 720 (30 days); the refresh then keeps it current.'
          }),
          F('refreshIntervalSeconds', 'Refresh interval (seconds)', {
            placeholder: '3600',
            help: 'How often the proxy pulls new journal rows. Default 3600 (hourly); minimum 300.'
          }),
          F('sourceTimezone', 'Server time zone (optional)', {
            span: 2,
            placeholder: 'e.g. Europe/Amsterdam',
            help: 'IANA zone of the PSS server when its journal reports local time. Leave blank to treat source times as UTC; explicit *_UTC columns remain authoritative.'
          })
        ],
        checks: [
          {
            id: 'journalAlarms',
            label: 'Mirror alarm events',
            help: 'Alarm activations, returns, and acknowledgements from the event journal.',
            default: true
          },
          {
            id: 'journalChanges',
            label: 'Mirror change events',
            help: 'Limit, parameter, and configuration changes recorded in the journal.',
            default: true
          },
          {
            id: 'journalSystem',
            label: 'Mirror system events',
            help: 'PSS system and audit entries. Usually low volume.',
            default: true
          }
        ]
      }
    }
    case 'experion':
      return {
        note: 'One-shot SQL login — the ERDB is probed and the DCS graph is written, then the session is dropped. If an EMDB database is reachable it is loaded too (enterprise/PCN assets + network). This does not persist a live database connection or register scrape tags.',
        fields: [
          F('server', 'SQL Server *', {
            span: 2,
            required: true,
            placeholder: 'localhost',
            help: 'Host, host,port (localhost,1433), or host\\instance. Prefer host or host,port — Tedious needs SQL Browser for named instances.'
          }),
          F('database', 'ERDB database *', {
            required: true,
            placeholder: 'ps_erdb'
          }),
          F('emdbDatabase', 'EMDB database', {
            placeholder: 'epks_emdb',
            help: 'Optional enterprise/PCN catalogue on the same SQL login. Defaults to epks_emdb; leave blank to skip. A probe failure never blocks the ERDB ingest.'
          }),
          F('username', 'SQL username *', {
            required: true,
            placeholder: 'erdb_reader'
          }),
          F('password', 'SQL password *', { required: true, type: 'password' })
        ]
      }
    case 'erp':
    case 'mes': {
      const viaIgnition = sub === 'ignition'
      const kind = id === 'erp' ? 'ERP' : 'MES'
      return {
        note: viaIgnition
          ? 'Read through the Ignition gateway’s database connection — no direct credentials needed.'
          : 'Direct database connection. Read-only credentials are enough.',
        fields: viaIgnition
          ? [
              F('connectionName', 'Ignition DB connection name *', {
                span: 2,
                required: true,
                placeholder: kind === 'ERP' ? 'ERP_PROD' : 'MES_PROD',
                help: 'As named under Ignition → Config → Databases → Connections.'
              })
            ]
          : [
              F('jdbcUrl', 'Connection URL *', {
                span: 2,
                required: true,
                placeholder: `jdbc:sqlserver://${kind.toLowerCase()}-db:1433;database=${kind}`
              }),
              F('dbUser', 'Database user *', {
                required: true,
                placeholder: 'axilon_reader'
              }),
              F('dbPassword', 'Database password', { type: 'password' })
            ]
      }
    }
    case 'unstructured':
      return {
        drop: {
          label: 'Ingest documents',
          helper:
            'Upload files to start a new ingest — P&ID drawings, manuals, SOPs, reports, or a zip.',
          accept: ''
        },
        fields: [
          F('additionalContext', 'Additional context (optional)', {
            span: 2,
            area: true,
            placeholder: 'Anything the ingest agent should know about these documents',
            help: 'Passed to the ingest agent alongside the files.'
          })
        ]
      }
    case 'tabular':
      return {
        drop: {
          label: 'Tabular file',
          helper:
            'Workbooks, CSVs, or scanned PDFs. Build a config from examples, or add rows to one you already have.',
          // The service's build accept list, so a file the wizard takes is one
          // the build (and a run of the finished config) will take too.
          accept: TABULAR_CONFIG_BUILD_ACCEPTED_EXTENSIONS.join(',')
        },
        fields: []
      }
    case 'servicenow':
      return {
        note: 'Credentials are stored encrypted and write-only. This registers the integration for the organization — it is not a site-scoped ingest job.',
        fields: [
          F('instanceName', 'Instance name *', {
            span: 2,
            required: true,
            placeholder: 'my-prod-instance',
            help: 'Your ServiceNow instance identifier (e.g. the subdomain of your instance URL).'
          }),
          F('apiKey', 'API key *', {
            span: 2,
            required: true,
            type: 'password',
            placeholder: 'Paste the ServiceNow API key',
            help: 'Stored encrypted. The key is write-only and is never displayed after saving.'
          })
        ],
        checks: [
          {
            id: 'workOrdersEnabled',
            label: 'Show in technician work orders',
            help: 'When on, open incidents from this instance appear in the Work Orders list so technicians can view, complete, and comment on them from Axilon.',
            default: false
          }
        ]
      }
    default:
      return { fields: [] }
  }
}

export const MOD_RESOURCES: Array<{
  id: ModifyResourceId
  label: string
  desc: string
  actions: ModifyActionOption[]
}> = [
  {
    id: 'scada',
    label: 'SCADA / gateway',
    desc: 'Gateway backups and the tag providers other resources bind to.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: true }
    ]
  },
  {
    id: 'opctags',
    label: 'OPC tag import',
    desc: 'Curated OPC addresses to register against an existing gateway.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'plc',
    label: 'PLC code',
    desc: 'Controller programs and the IP / network map their tags resolve through.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: true },
      { id: 'remove', available: false }
    ]
  },
  {
    id: 'ipmap',
    label: 'IP / network map',
    desc: 'Devices and OPC servers mapped to the PLCs that serve their tags.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: true }
    ]
  },
  {
    id: 'robotics',
    label: 'Robotics code',
    desc: 'Robot controller backups and the controller to PLC mapping.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: false },
      { id: 'remove', available: false }
    ]
  },
  {
    id: 'vision',
    label: 'Vision system code',
    desc: 'Cognex In-Sight and DataMan backups and the camera → PLC mapping.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: false },
      { id: 'remove', available: false }
    ]
  },
  {
    id: 'mtconnect',
    label: 'MTConnect server',
    desc: 'Agent probe, device tree, and the DataItems registered for streaming.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: false },
      { id: 'remove', available: false }
    ]
  },
  {
    id: 'pi',
    label: 'PI historian',
    desc: 'PI Web API Data Archive point discovery and historian registration.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'canary',
    label: 'Canary historian',
    desc: 'Canary Labs Views browse, display folders, and read-through historian registration.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'dbdump',
    label: 'DB dump',
    desc: 'Excel or CSV dump queried as a stand-in database.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'sql',
    label: 'SQL database',
    desc: 'Persistent postgres / SQL Server / MySQL login. Same standalone Database node as a spreadsheet dump; credentials stored for later queries.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'soap',
    label: 'SOAP web service',
    desc: 'WSDL endpoint registration and operation contract ingest.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'pss',
    label: 'Hexagon PlantState Suite',
    desc: 'PSS / PSI SOAP service mirrored into managed SQL tables with a scheduled journal refresh.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: true }
    ]
  },
  {
    id: 'experion',
    label: 'Experion ERDB + EMDB',
    desc: 'Honeywell Experion PKS control (ERDB) + optional enterprise/PCN (EMDB). One-shot graph ingest — no live connection.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: false },
      { id: 'remove', available: false }
    ]
  },
  {
    id: 'srs',
    label: 'Safety Requirements Specification',
    desc: 'SRS Access database (.accdb) ingested as ProtectiveFunction nodes and SRS-only edges.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'safetymanager',
    label: 'Safety Manager dump',
    desc: 'Honeywell Safety Manager — legacy Excel dump or Safety Builder 15.2 SQL database.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'safetymap',
    label: 'Safety Manager ↔ SRS mapping',
    desc: 'Link existing SRS functions and equipment to existing Safety Manager FLDs and points.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'tagmap',
    label: 'Create Tag Mapping',
    desc: 'Choose a mapping type, generate grammars from existing site data, and preview links before applying.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'tabular',
    label: 'Tabular file',
    desc: 'Build a parser config from examples or add rows with an existing config.',
    actions: [{ id: 'add', available: true }]
  },
  {
    id: 'docs',
    label: 'Documentation',
    desc: 'P&ID drawings, manuals, SOPs, and reports.',
    actions: [
      { id: 'add', available: true },
      { id: 'update', available: false }
    ]
  },
  {
    id: 'servicenow',
    label: 'ServiceNow',
    desc: 'Connect a ServiceNow instance for incidents and technician work orders.',
    actions: [{ id: 'add', available: true }]
  }
]

export type ModifyResourceSpec = (typeof MOD_RESOURCES)[number]

export function isModifyActionAvailable(resource: ModifyResourceId, action: ModifyAction): boolean {
  const res = MOD_RESOURCES.find((r) => r.id === resource)
  return res?.actions.some((a) => a.id === action && a.available) ?? false
}

export function defaultModifyAction(resource: ModifyResourceId): ModifyAction | null {
  const res = MOD_RESOURCES.find((r) => r.id === resource)
  return res?.actions.find((a) => a.available)?.id ?? null
}

export const ACTION_LABEL: Record<ModifyAction, string> = {
  add: 'Add',
  update: 'Update',
  remove: 'Delete'
}

export const ADD_SOURCES: Partial<Record<ModifyResourceId, SourceId[]>> = {
  scada: ['scada'],
  opctags: ['opctags'],
  plc: ['plc', 'ipmap'],
  ipmap: ['ipmap'],
  robotics: ['robotics', 'roboticsmap'],
  vision: ['vision', 'visionmap'],
  mtconnect: ['mtconnect'],
  pi: ['pi'],
  canary: ['canary'],
  dbdump: ['dbdump'],
  sql: ['sql'],
  soap: ['soap'],
  pss: ['pss'],
  experion: ['experion'],
  srs: ['srs'],
  safetymanager: ['safetymanager'],
  safetymap: ['safetymap'],
  tagmap: ['tagmap'],
  tabular: ['tabular'],
  docs: ['unstructured'],
  servicenow: ['servicenow']
}

export const UPDATE_SOURCES: Partial<Record<ModifyResourceId, SourceId[]>> = {
  scada: ['scada'],
  ipmap: ['ipmap'],
  // Update PLC is a no-upload pull-sync picker (StepUpdatePlc), not a
  // file-collect flow — it routes through its own `plcupdate` modStage.
  plc: ['plcupdate'],
  pss: ['pssupdate']
}

/** Sources the wizard can create jobs for from prep (headless launch). */
export const LAUNCHABLE: Partial<
  Record<
    SourceId,
    {
      kind: UploadableIngestJobKind | 'integration'
      documentType?: 'IP' | 'ROBOTICS_PLC' | 'VISION_PLC'
    }
  >
> = {
  scada: { kind: 'SCADA' },
  opctags: { kind: 'TagImport' },
  plc: { kind: 'PLC' },
  // Update PLC creates `plc_update` ingest jobs but StepRun renders its own
  // pull-sync status panel, so it carries no JobDetailPanel-backed kind.
  plcupdate: { kind: 'integration' },
  pssupdate: { kind: 'PSS' },
  ipmap: { kind: 'StructuredDocument', documentType: 'IP' },
  robotics: { kind: 'Robotics' },
  roboticsmap: { kind: 'StructuredDocument', documentType: 'ROBOTICS_PLC' },
  vision: { kind: 'Vision' },
  visionmap: { kind: 'StructuredDocument', documentType: 'VISION_PLC' },
  mtconnect: { kind: 'MTConnect' },
  pi: { kind: 'PI' },
  canary: { kind: 'Canary' },
  dbdump: { kind: 'TabularDump' },
  sql: { kind: 'Sql' },
  soap: { kind: 'SOAP' },
  pss: { kind: 'PSS' },
  experion: { kind: 'Experion' },
  srs: { kind: 'SRS' },
  safetymanager: { kind: 'SafetyManager' },
  safetymap: { kind: 'TagMapping' },
  tagmap: { kind: 'TagMapping' },
  unstructured: { kind: 'Unstructured' },
  tabular: { kind: 'MappedTabularIngest' },
  servicenow: { kind: 'integration' }
}

export function defaultSub(id: SourceId): SubOptionId | undefined {
  if (id === 'scada') return 'ignition'
  if (id === 'erp' || id === 'mes') return 'ignition'
  if (id === 'safetymanager') return 'legacy'
  if (id === 'sql') return 'postgres'
  if (id === 'canary') return 'api_token'
  return undefined
}

export function emptyPrep(id: SourceId, sub?: SubOptionId): import('./types').PrepValues {
  const spec = specFor(id, sub, 'Yaskawa', null, 'webdev')
  const checks: Record<string, boolean> = {}
  for (const c of spec.checks ?? []) checks[c.id] = c.default
  return {
    file: null,
    files: [],
    fields: {},
    checks,
    vendor: id === 'robotics' ? 'Yaskawa' : undefined,
    tagMode:
      id === 'pi' || id === 'canary'
        ? 'standalone'
        : id === 'soap' || id === 'pss'
          ? 'none'
          : id === 'opctags' || (id === 'scada' && sub !== 'aveva')
            ? 'webdev'
            : undefined
  }
}
